// dashboard/src/pages/auth/VerifyPage.tsx

import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { resendSignUpCode } from 'aws-amplify/auth'
import { Loader2, Mail, Check } from 'lucide-react'

export default function VerifyPage() {
  const [code, setCode] = useState('')
  const [verified, setVerified] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [countdown, setCountdown] = useState(3)
  const { confirmSignUp, isLoading, error, clearError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const email = location.state?.email || ''

  useEffect(() => {
    if (!email) {
      navigate('/register')
    }
  }, [email, navigate])

  // Auto-redirect after verification success
  useEffect(() => {
    if (verified) {
      clearError() // Ensure error is cleared before redirect
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            navigate('/login', { state: { email, verified: true } })
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [verified, navigate, email])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()

    try {
      await confirmSignUp(email, code)
      // Verification successful - set verified state and navigate directly
      setVerified(true)
      clearError()
      // Small delay to show success UI before redirect
      setTimeout(() => {
        navigate('/login', { state: { email, verified: true } })
      }, 500)
    } catch (err: any) {
      // Handle "already confirmed" case - redirect to login
      if (
        err?.message?.includes('CONFIRMED') ||
        err?.message?.includes('Current status is CONFIRMED') ||
        err?.name === 'NotAuthorizedException'
      ) {
        clearError()
        navigate('/login', { state: { email, verified: true } })
        return
      }
      // Other errors handled by hook
    }
  }

  const handleResendCode = async () => {
    try {
      setResending(true)
      setResendSuccess(false)
      await resendSignUpCode({ username: email })
      setResendSuccess(true)
    } catch (err) {
      console.error('Resend code error:', err)
    } finally {
      setResending(false)
    }
  }

  if (verified) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Email verified!</h2>
        <p className="text-gray-600 mb-4">Your email has been successfully verified.</p>
        <p className="text-gray-500 mb-8">
          Redirecting to login in <span className="font-bold text-yellow-600">{countdown}</span> seconds...
        </p>
        <Link
          to="/login"
          state={{ email, verified: true }}
          className="inline-flex items-center justify-center w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors"
        >
          Sign in now
        </Link>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-yellow-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Check your email</h2>
        <p className="text-gray-600 mt-2">
          We've sent a verification code to<br />
          <span className="font-medium text-gray-900">{email}</span>
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2 text-center">
            Verification code
          </label>
          <input
            id="code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors text-center text-3xl tracking-[0.5em] font-mono"
            placeholder="000000"
            maxLength={6}
            required
            autoFocus
          />
          <p className="mt-2 text-sm text-gray-500 text-center">
            Enter the 6-digit code from your email
          </p>
        </div>

        <button
          type="submit"
          disabled={isLoading || code.length !== 6}
          className="w-full py-3 px-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify email'
          )}
        </button>
      </form>

      <div className="mt-8 text-center">
        {resendSuccess ? (
          <p className="text-green-600">
            New code sent! Check your email.
          </p>
        ) : (
          <p className="text-gray-600">
            Didn't receive the email?{' '}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resending}
              className="text-yellow-600 hover:text-yellow-700 font-medium disabled:opacity-50"
            >
              {resending ? 'Sending...' : 'Click to resend'}
            </button>
          </p>
        )}

      </div>

      <div className="mt-4 text-center">
        <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700">
          Back to sign in
        </Link>
      </div>
    </div>
  )
}
